import type { Context } from "hono";

export type AuthRateLimitRoute = "sign-in" | "sign-up" | "token";

export interface AuthRateLimitRule {
  limit: number;
  windowMs: number;
}

export interface AuthRateLimitResult {
  allowed: boolean;
  limit: number;
  remaining: number;
  resetAt: number;
  retryAfterSec: number;
}

type AuthRateLimitRules = Partial<Record<AuthRateLimitRoute, AuthRateLimitRule>>;

const DEFAULT_RULES: Record<AuthRateLimitRoute, AuthRateLimitRule> = {
  "sign-in": { limit: 10, windowMs: 10 * 60 * 1000 },
  "sign-up": { limit: 5, windowMs: 10 * 60 * 1000 },
  token: { limit: 120, windowMs: 5 * 60 * 1000 },
};
const MAX_BUCKETS = 10_000;

let buckets = new Map<string, { count: number; resetAt: number }>();

export function resetAuthRateLimitForTests(): void {
  buckets = new Map();
}

export function matchAuthRateLimitRoute(method: string, pathname: string): AuthRateLimitRoute | null {
  if (method.toUpperCase() !== "POST") return null;
  const normalized = pathname.replace(/\/+$/, "");
  if (normalized === "/api/auth/sign-in") return "sign-in";
  if (normalized === "/api/auth/sign-up") return "sign-up";
  if (normalized === "/api/auth/token") return "token";
  return null;
}

function getClientIdentity(c: Context): string {
  const env = c.env as { ip?: { address?: unknown } } | undefined;
  const peerAddress = typeof env?.ip?.address === "string" ? env.ip.address.trim() : "";
  if (peerAddress && !isLocalPeerAddress(peerAddress)) return peerAddress;

  const realIp = c.req.header("x-real-ip")?.trim();
  if (realIp) return realIp;

  const forwardedChain = (c.req.header("x-forwarded-for") || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  return forwardedChain.at(-1) || peerAddress || "unknown";
}

function isLocalPeerAddress(address: string): boolean {
  return (
    address === "127.0.0.1" ||
    address === "::1" ||
    address === "0:0:0:0:0:0:0:1" ||
    address.startsWith("::ffff:127.")
  );
}

function pruneExpired(now: number): void {
  for (const [key, bucket] of buckets) {
    if (bucket.resetAt <= now) buckets.delete(key);
  }
}

function enforceBucketCap(now: number): void {
  if (buckets.size <= MAX_BUCKETS) return;
  pruneExpired(now);
  while (buckets.size > MAX_BUCKETS) {
    const oldest = buckets.keys().next().value;
    if (!oldest) return;
    buckets.delete(oldest);
  }
}

export function consumeAuthRateLimit(params: {
  route: AuthRateLimitRoute;
  identity: string;
  now?: number;
  rule?: AuthRateLimitRule;
}): AuthRateLimitResult {
  const now = params.now ?? Date.now();
  const rule = params.rule ?? DEFAULT_RULES[params.route];
  const key = `${params.route}:${params.identity}`;
  let bucket = buckets.get(key);
  if (!bucket || bucket.resetAt <= now) {
    bucket = { count: 0, resetAt: now + rule.windowMs };
    buckets.set(key, bucket);
  }
  enforceBucketCap(now);

  const retryAfterSec = Math.max(1, Math.ceil((bucket.resetAt - now) / 1000));
  if (bucket.count >= rule.limit) {
    return { allowed: false, limit: rule.limit, remaining: 0, resetAt: bucket.resetAt, retryAfterSec };
  }

  bucket.count++;
  return {
    allowed: true,
    limit: rule.limit,
    remaining: Math.max(0, rule.limit - bucket.count),
    resetAt: bucket.resetAt,
    retryAfterSec,
  };
}

function setRateLimitHeaders(c: Context, result: AuthRateLimitResult): void {
  c.header("X-RateLimit-Limit", String(result.limit));
  c.header("X-RateLimit-Remaining", String(result.remaining));
  c.header("X-RateLimit-Reset", String(Math.ceil(result.resetAt / 1000)));
}

export function enforceAuthRateLimit(
  c: Context,
  pathname: string,
  options: { now?: () => number; rules?: AuthRateLimitRules } = {},
): Response | null {
  const route = matchAuthRateLimitRoute(c.req.method, pathname);
  if (!route) return null;

  const result = consumeAuthRateLimit({
    route,
    identity: getClientIdentity(c),
    now: options.now?.(),
    rule: options.rules?.[route],
  });
  setRateLimitHeaders(c, result);
  if (result.allowed) return null;

  c.header("Retry-After", String(result.retryAfterSec));
  return c.json(
    { error: "Too many requests", message: "Too many authentication attempts. Try again later." },
    429,
  );
}
